import PropTypes from 'prop-types';
import { Box, Typography, Button, ButtonGroup, Tooltip } from '@mui/material';
import { useState, useMemo } from 'react';
import { Iconify } from 'src/components';

const METRICS = [
    { key: 'leads', label: 'Leads', color: '#3b82f6' },
    { key: 'consultations', label: 'Consultations', color: '#8b5cf6' },
    { key: 'treatments', label: 'Treatments', color: '#10b981' },
    { key: 'revenue', label: 'Revenue', color: '#f59e0b' },
];

export default function TrendChart({ data, title, defaultMetric }) {
    const [selectedMetric, setSelectedMetric] = useState(defaultMetric);

    const chartConfig = useMemo(() => {
        const metricConfig = METRICS.find((m) => m.key === selectedMetric) || METRICS[0];
        const values = data.map((d) => d[selectedMetric] || 0);
        const maxValue = Math.max(...values, 1);
        const minValue = Math.min(...values);

        return {
            ...metricConfig,
            values,
            maxValue,
            minValue,
        };
    }, [data, selectedMetric]);

    const formatValue = (value) => {
        if (selectedMetric === 'revenue') {
            if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
            if (value >= 1000) return `£${(value / 1000).toFixed(1)}K`;
            return `£${value}`;
        }
        return value?.toLocaleString() || 0;
    };

    // SVG chart dimensions
    const width = 800;
    const height = 300;
    const padding = { top: 20, right: 40, bottom: 40, left: 60 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Calculate points for the line
    const points = useMemo(() => {
        if (data.length === 0) return [];

        const xStep = chartWidth / (data.length - 1 || 1);
        const range = chartConfig.maxValue - chartConfig.minValue || 1;

        return data.map((d, i) => {
            const x = padding.left + i * xStep;
            const value = d[selectedMetric] || 0;
            const y = padding.top + chartHeight - ((value - chartConfig.minValue) / range) * chartHeight;
            return { x, y, value, label: d.month || d.label };
        });
    }, [data, selectedMetric, chartConfig, chartWidth, chartHeight]);

    // Create path for the line
    const linePath = useMemo(() => {
        if (points.length === 0) return '';
        return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    }, [points]);

    // Create area path for gradient fill
    const areaPath = useMemo(() => {
        if (points.length === 0) return '';
        const linePoints = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
        return `${linePoints} L ${points[points.length - 1]?.x} ${padding.top + chartHeight} L ${padding.left} ${padding.top + chartHeight} Z`;
    }, [points, chartHeight]);

    // Y-axis labels
    const yAxisLabels = useMemo(() => {
        const range = chartConfig.maxValue - chartConfig.minValue || 1;
        const steps = 5;
        return Array.from({ length: steps + 1 }, (_, i) => {
            const value = chartConfig.minValue + (range * (steps - i)) / steps;
            const y = padding.top + (chartHeight * i) / steps;
            return { value, y };
        });
    }, [chartConfig, chartHeight]);

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                p: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                mb: 3,
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                }}
            >
                <Tooltip title="Monthly trends for your selected metric over time." placement="bottom" arrow
                    slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                        <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
                            {title}
                        </Typography>
                        <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                    </Box>
                </Tooltip>

                <ButtonGroup variant="outlined" size="small">
                    {METRICS.map((metric) => (
                        <Button
                            key={metric.key}
                            onClick={() => setSelectedMetric(metric.key)}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                px: 2,
                                fontSize: '13px',
                                ...(selectedMetric === metric.key && {
                                    bgcolor: metric.color,
                                    color: 'white',
                                    borderColor: metric.color,
                                    '&:hover': {
                                        bgcolor: metric.color,
                                        borderColor: metric.color,
                                    },
                                }),
                            }}
                        >
                            {metric.label}
                        </Button>
                    ))}
                </ButtonGroup>
            </Box>

            {/* Chart */}
            {data.length > 0 ? (
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto', minHeight: '250px' }}>
                        {/* Gradient definition */}
                        <defs>
                            <linearGradient id={`gradient-${selectedMetric}`} x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor={chartConfig.color} stopOpacity="0.3" />
                                <stop offset="100%" stopColor={chartConfig.color} stopOpacity="0.05" />
                            </linearGradient>
                        </defs>

                        {/* Grid lines */}
                        {yAxisLabels.map((label, i) => (
                            <line
                                key={i}
                                x1={padding.left}
                                y1={label.y}
                                x2={width - padding.right}
                                y2={label.y}
                                stroke="#e2e8f0"
                                strokeWidth="1"
                            />
                        ))}

                        {/* Y-axis labels */}
                        {yAxisLabels.map((label, i) => (
                            <text
                                key={i}
                                x={padding.left - 10}
                                y={label.y + 4}
                                textAnchor="end"
                                fontSize="11"
                                fill="#64748b"
                            >
                                {formatValue(Math.round(label.value))}
                            </text>
                        ))}

                        {/* X-axis labels */}
                        {points.map((point, i) => (
                            <text
                                key={i}
                                x={point.x}
                                y={height - padding.bottom + 20}
                                textAnchor="middle"
                                fontSize="11"
                                fill="#64748b"
                            >
                                {point.label}
                            </text>
                        ))}

                        {/* Area fill */}
                        <path
                            d={areaPath}
                            fill={`url(#gradient-${selectedMetric})`}
                        />

                        {/* Line */}
                        <path
                            d={linePath}
                            fill="none"
                            stroke={chartConfig.color}
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />

                        {/* Data points */}
                        {points.map((point, i) => (
                            <g key={i}>
                                <circle
                                    cx={point.x}
                                    cy={point.y}
                                    r="6"
                                    fill="white"
                                    stroke={chartConfig.color}
                                    strokeWidth="3"
                                />
                                {/* Tooltip on hover - simplified version */}
                                <title>{`${point.label}: ${formatValue(point.value)}`}</title>
                            </g>
                        ))}
                    </svg>
                </Box>
            ) : (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ color: '#64748b' }}>
                        No trend data available
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

TrendChart.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            month: PropTypes.string,
            label: PropTypes.string,
            leads: PropTypes.number,
            consultations: PropTypes.number,
            treatments: PropTypes.number,
            revenue: PropTypes.number,
        })
    ).isRequired,
    title: PropTypes.string,
    defaultMetric: PropTypes.oneOf(['leads', 'consultations', 'treatments', 'revenue']),
};

TrendChart.defaultProps = {
    title: 'Performance Trends',
    defaultMetric: 'leads',
};
